/**
 * Admin Settings (P29 + P30 strip).
 *
 * Three live tabs: Workgroups, OAuth Clients, Virtual Servers — all reading
 * directly from the control-plane registry. Wireframe Retention +
 * Compliance panels were removed because no V1 entity backs them.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Globe2, KeyRound, Plus, Trash2, UserPlus, Users } from 'lucide-react';

import {
  ApiError,
  useClients,
  useDeletePortalMember,
  usePortalMembers,
  useUpsertPortalMember,
  useVirtualServersPaged,
  useWorkgroups,
} from '@/api/index.js';
import { useAuth } from '@/auth/AuthContext.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { OAuthClientDetail } from '@/components/console/drilldowns/OAuthClientDetail.js';
import { VirtualServerDetail } from '@/components/console/drilldowns/VirtualServerDetail.js';
import { WorkgroupDetail } from '@/components/console/drilldowns/WorkgroupDetail.js';
import { Required } from '@/components/console/Required.js';
import { useDebouncedUrlParam, useUrlInt, useUrlParam } from '@/hooks/useUrlState.js';

export type AdminTab = 'Members' | 'Workgroups' | 'OAuth Clients' | 'Virtual Servers';

const PAGE_SIZE = 50;

export default function AdminSettings({
  defaultTab = 'Members',
}: { defaultTab?: AdminTab } = {}): JSX.Element {
  // The route (/admin/workgroups, /admin/clients, /admin/virtual-servers,
  // /admin/settings) determines what's shown. defaultTab comes from the
  // route entry in App.tsx; we never let a ?tab= query override it,
  // because that creates URL/UI mismatches (path says clients, query says
  // virtual-servers). Each admin route shows exactly one section.
  const tab: AdminTab = defaultTab;

  // Workgroups: paged + searchable (scale-ready for 1000s of WGs).
  // All state in URL: search (debounced), offset, reviewer filter.
  const [wgQuery, setWgQuery, debouncedWgQuery] = useDebouncedUrlParam('wg_q');
  const [wgOffset, setWgOffset] = useUrlInt('wg_offset', 0);
  const [wgReviewerParam, setWgReviewerParam] = useUrlParam('wg_reviewer', '');
  const wgReviewerOnly = wgReviewerParam === 'true';
  const setWgReviewerOnly = (v: boolean): void => setWgReviewerParam(v ? 'true' : '');
  // Reset offset when filters change.
  useEffect(() => {
    if (wgOffset !== 0) setWgOffset(0);
  }, [debouncedWgQuery, wgReviewerOnly]);
  const wgs = useWorkgroups({
    q: debouncedWgQuery || undefined,
    reviewer: wgReviewerOnly ? 'true' : undefined,
    limit: PAGE_SIZE,
    offset: wgOffset,
  });

  // Drawer state in URL: `?wg=new` for create, `?wg=<id>` for detail, absent
  // for closed. Browser back closes the drawer; the URL is shareable.
  const [wgParam, setWgParam] = useUrlParam('wg', '');
  const wgDrawer = useMemo(
    () =>
      wgParam === ''
        ? null
        : wgParam === 'new'
          ? ({ mode: 'create' } as const)
          : ({ mode: 'detail', id: wgParam } as const),
    [wgParam],
  );
  const setWgDrawer = (next: { mode: 'detail' | 'create'; id?: string } | null): void => {
    if (!next) setWgParam('');
    else if (next.mode === 'create') setWgParam('new');
    else setWgParam(next.id ?? '');
  };

  // OAuth Clients: same paged + searchable shape, URL-driven.
  const [clientQuery, setClientQuery, debouncedClientQuery] = useDebouncedUrlParam('client_q');
  const [clientOffset, setClientOffset] = useUrlInt('client_offset', 0);
  const [clientType, setClientType] = useUrlParam('client_type', '');
  useEffect(() => {
    if (clientOffset !== 0) setClientOffset(0);
  }, [debouncedClientQuery, clientType]);
  const clients = useClients({
    q: debouncedClientQuery || undefined,
    type: clientType || undefined,
    limit: PAGE_SIZE,
    offset: clientOffset,
  });
  const [clientParam, setClientParam] = useUrlParam('client', '');
  const clientDrawer = useMemo(
    () =>
      clientParam === ''
        ? null
        : clientParam === 'new'
          ? ({ mode: 'create' } as const)
          : ({ mode: 'detail', id: clientParam } as const),
    [clientParam],
  );
  const setClientDrawer = (next: { mode: 'detail' | 'create'; id?: string } | null): void => {
    if (!next) setClientParam('');
    else if (next.mode === 'create') setClientParam('new');
    else setClientParam(next.id ?? '');
  };

  // Virtual Servers: paged + searchable, URL-driven.
  const [vsQuery, setVsQuery, debouncedVsQuery] = useDebouncedUrlParam('vs_q');
  const [vsOffset, setVsOffset] = useUrlInt('vs_offset', 0);
  const [vsStatus, setVsStatus] = useUrlParam('vs_status', '');
  useEffect(() => {
    if (vsOffset !== 0) setVsOffset(0);
  }, [debouncedVsQuery, vsStatus]);
  const vs = useVirtualServersPaged({
    q: debouncedVsQuery || undefined,
    status: vsStatus || undefined,
    limit: PAGE_SIZE,
    offset: vsOffset,
  });
  const [vsParam, setVsParam] = useUrlParam('vs', '');
  const vsDrawer = useMemo(
    () =>
      vsParam === ''
        ? null
        : vsParam === 'new'
          ? ({ mode: 'create' } as const)
          : ({ mode: 'detail', id: vsParam } as const),
    [vsParam],
  );
  const setVsDrawer = (next: { mode: 'detail' | 'create'; id?: string } | null): void => {
    if (!next) setVsParam('');
    else if (next.mode === 'create') setVsParam('new');
    else setVsParam(next.id ?? '');
  };

  const headerTitle =
    tab === 'Members'
      ? 'Portal Members'
      : tab === 'Workgroups'
        ? 'Workgroups'
        : tab === 'OAuth Clients'
          ? 'OAuth Clients'
          : 'Virtual Servers';
  const headerDescription =
    tab === 'Members'
      ? 'Stanford SSO confirms hd=stanford.edu, then this allowlist decides who can sign in.'
      : tab === 'Workgroups'
        ? 'Stanford workgroups — the unit of authorization. Reviewer WGs decide approvals.'
        : tab === 'OAuth Clients'
          ? 'AI / agent clients allowed to call the gateway (Claude Desktop, Cursor, S2S).'
          : 'Curated tool bundles served at /v/<slug>/mcp for specific consumers.';

  return (
    <>
      <PageHeader eyebrow="Admin" title={headerTitle} description={headerDescription} />

      {tab === 'Members' && <MembersTab />}

      {tab === 'Workgroups' && (
        <SectionCard
          icon={Users}
          title={`Workgroups (${wgs.data?.total ?? 0})`}
          text="Stanford workgroups — the unit of authorization in the gateway. Reviewer WGs decide approvals."
        >
          {/* Toolbar — scale-friendly: server-side q/limit/offset */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <input
              placeholder="Search id / name / description…"
              value={wgQuery}
              onChange={(e) => setWgQuery(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                background: 'transparent',
                color: 'inherit',
                flex: '1 1 240px',
                minWidth: 0,
              }}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={wgReviewerOnly}
                onChange={(e) => setWgReviewerOnly(e.target.checked)}
              />
              Reviewer WGs only
            </label>
            <button
              type="button"
              className="primary"
              onClick={() => setWgDrawer({ mode: 'create' })}
              style={primaryBtnStyle}
            >
              <Plus size={14} style={{ marginRight: 6 }} />
              New workgroup
            </button>
          </div>

          <AsyncShell
            isLoading={wgs.isLoading}
            isError={wgs.isError}
            isEmpty={(wgs.data?.items.length ?? 0) === 0}
            error={wgs.error}
            loadingTitle="Loading workgroups"
            errorTitle="Workgroups API unreachable"
            emptyTitle={debouncedWgQuery ? 'No matches' : 'No workgroups yet'}
            emptyHint={
              debouncedWgQuery ? (
                <>
                  Try a different query, or{' '}
                  <button type="button" onClick={() => setWgQuery('')}>
                    clear
                  </button>
                  .
                </>
              ) : (
                <>
                  Click <strong>New workgroup</strong> to create the first one.
                </>
              )
            }
          >
            <DataTable
              columns={['id', 'display_name', 'owner', 'reviewer', 'source']}
              rows={
                (wgs.data?.items ?? []).map((w) => ({
                  id: w.id,
                  display_name: w.display_name,
                  owner: w.owner_sunet ?? '—',
                  reviewer: w.is_reviewer_wg ? 'yes' : 'no',
                  source: w.source,
                })) as readonly DataRow[]
              }
              onRow={(r) => setWgDrawer({ mode: 'detail', id: String(r.id) })}
            />
            {/* Paging footer */}
            {(wgs.data?.total ?? 0) > PAGE_SIZE && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 10,
                  fontSize: 13,
                }}
              >
                <span className="subtle">
                  Showing {wgOffset + 1}–{Math.min(wgOffset + PAGE_SIZE, wgs.data?.total ?? 0)} of{' '}
                  {wgs.data?.total ?? 0}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    disabled={wgOffset === 0}
                    onClick={() => setWgOffset(Math.max(0, wgOffset - PAGE_SIZE))}
                    style={pagerBtnStyle(wgOffset === 0)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={wgOffset + PAGE_SIZE >= (wgs.data?.total ?? 0)}
                    onClick={() => setWgOffset(wgOffset + PAGE_SIZE)}
                    style={pagerBtnStyle(wgOffset + PAGE_SIZE >= (wgs.data?.total ?? 0))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </AsyncShell>

          <WorkgroupDetail
            open={wgDrawer != null}
            onClose={() => setWgDrawer(null)}
            mode={wgDrawer?.mode ?? 'detail'}
            wgId={wgDrawer?.mode === 'detail' ? wgDrawer?.id : undefined}
            onPersisted={(id) => setWgDrawer({ mode: 'detail', id })}
          />
        </SectionCard>
      )}

      {tab === 'OAuth Clients' && (
        <SectionCard
          icon={KeyRound}
          title={`OAuth clients (${clients.data?.total ?? 0})`}
          text="AI / agent clients allowed to call the gateway."
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <input
              placeholder="Search id / name…"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                background: 'transparent',
                color: 'inherit',
                flex: '1 1 240px',
                minWidth: 0,
              }}
            />
            <select
              value={clientType}
              onChange={(e) => setClientType(e.target.value)}
              aria-label="Filter OAuth clients by type"
              style={{
                padding: '6px 10px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                background: 'transparent',
                color: 'inherit',
              }}
            >
              <option value="">All types</option>
              <option value="claude_desktop">claude_desktop</option>
              <option value="claude_ai">claude_ai</option>
              <option value="s2s">s2s</option>
              <option value="dev">dev</option>
            </select>
            <button
              type="button"
              className="primary"
              onClick={() => setClientDrawer({ mode: 'create' })}
              style={primaryBtnStyle}
            >
              <Plus size={14} style={{ marginRight: 6 }} />
              New OAuth client
            </button>
          </div>

          <AsyncShell
            isLoading={clients.isLoading}
            isError={clients.isError}
            isEmpty={(clients.data?.items.length ?? 0) === 0}
            error={clients.error}
            loadingTitle="Loading clients"
            errorTitle="Clients API unreachable"
            emptyTitle={debouncedClientQuery || clientType ? 'No matches' : 'No OAuth clients yet'}
            emptyHint={
              <>
                Click <strong>New OAuth client</strong> to register one.
              </>
            }
          >
            <DataTable
              columns={['name', 'type', 'owner', 'scopes', 'status']}
              rows={
                (clients.data?.items ?? []).map((c) => ({
                  name: `${c.displayName} (${c.id.slice(0, 12)}…)`,
                  type: c.type,
                  owner: c.ownerWgId,
                  scopes: c.allowedScopes.join(', ') || '(none)',
                  status: c.status,
                })) as readonly DataRow[]
              }
              onRow={(r) => {
                const id = (clients.data?.items ?? []).find(
                  (c) => `${c.displayName} (${c.id.slice(0, 12)}…)` === String(r.name),
                )?.id;
                if (id) setClientDrawer({ mode: 'detail', id });
              }}
            />
            {(clients.data?.total ?? 0) > PAGE_SIZE && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 10,
                  fontSize: 13,
                }}
              >
                <span className="subtle">
                  Showing {clientOffset + 1}–
                  {Math.min(clientOffset + PAGE_SIZE, clients.data?.total ?? 0)} of{' '}
                  {clients.data?.total ?? 0}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    disabled={clientOffset === 0}
                    onClick={() => setClientOffset(Math.max(0, clientOffset - PAGE_SIZE))}
                    style={pagerBtnStyle(clientOffset === 0)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={clientOffset + PAGE_SIZE >= (clients.data?.total ?? 0)}
                    onClick={() => setClientOffset(clientOffset + PAGE_SIZE)}
                    style={pagerBtnStyle(clientOffset + PAGE_SIZE >= (clients.data?.total ?? 0))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </AsyncShell>

          <OAuthClientDetail
            open={clientDrawer != null}
            onClose={() => setClientDrawer(null)}
            mode={clientDrawer?.mode ?? 'detail'}
            clientId={clientDrawer?.mode === 'detail' ? clientDrawer?.id : undefined}
            onPersisted={(id) => setClientDrawer({ mode: 'detail', id })}
          />
        </SectionCard>
      )}

      {tab === 'Virtual Servers' && (
        <SectionCard
          icon={Globe2}
          title={`Virtual servers (${vs.data?.total ?? 0})`}
          text="Curated MCP endpoints. Each bundles a subset of tools for a specific consumer or team."
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <input
              placeholder="Search slug / name / description…"
              value={vsQuery}
              onChange={(e) => setVsQuery(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                background: 'transparent',
                color: 'inherit',
                flex: '1 1 240px',
                minWidth: 0,
              }}
            />
            <select
              value={vsStatus}
              onChange={(e) => setVsStatus(e.target.value)}
              aria-label="Filter virtual servers by status"
              style={{
                padding: '6px 10px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                background: 'transparent',
                color: 'inherit',
              }}
            >
              <option value="">All statuses</option>
              <option value="draft">draft</option>
              <option value="active">active</option>
              <option value="retired">retired</option>
            </select>
            <button
              type="button"
              className="primary"
              onClick={() => setVsDrawer({ mode: 'create' })}
              style={primaryBtnStyle}
            >
              <Plus size={14} style={{ marginRight: 6 }} />
              New virtual server
            </button>
          </div>

          <AsyncShell
            isLoading={vs.isLoading}
            isError={vs.isError}
            isEmpty={(vs.data?.items.length ?? 0) === 0}
            error={vs.error}
            loadingTitle="Loading virtual servers"
            errorTitle="Virtual servers API unreachable"
            emptyTitle={debouncedVsQuery || vsStatus ? 'No matches' : 'No virtual servers yet'}
            emptyHint={
              <>
                Click <strong>New virtual server</strong> to curate the first bundle.
              </>
            }
          >
            <DataTable
              columns={['slug', 'name', 'endpoint', 'tools', 'clients', 'status']}
              rows={
                (vs.data?.items ?? []).map((v) => {
                  const composition = (v.composition ?? []) as { exposure?: string }[];
                  const included = composition.filter((c) => c.exposure === 'included').length;
                  return {
                    slug: v.slug,
                    name: v.name,
                    endpoint: v.endpointUrl.replace(window.location.origin, ''),
                    tools: `${included} included / ${composition.length} total`,
                    clients: v.allowedClientIds.length,
                    status: v.status,
                  };
                }) as readonly DataRow[]
              }
              onRow={(r) => {
                const id = (vs.data?.items ?? []).find((v) => v.slug === String(r.slug))?.id;
                if (id) setVsDrawer({ mode: 'detail', id });
              }}
            />
            {(vs.data?.total ?? 0) > PAGE_SIZE && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 10,
                  fontSize: 13,
                }}
              >
                <span className="subtle">
                  Showing {vsOffset + 1}–{Math.min(vsOffset + PAGE_SIZE, vs.data?.total ?? 0)} of{' '}
                  {vs.data?.total ?? 0}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    disabled={vsOffset === 0}
                    onClick={() => setVsOffset(Math.max(0, vsOffset - PAGE_SIZE))}
                    style={pagerBtnStyle(vsOffset === 0)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={vsOffset + PAGE_SIZE >= (vs.data?.total ?? 0)}
                    onClick={() => setVsOffset(vsOffset + PAGE_SIZE)}
                    style={pagerBtnStyle(vsOffset + PAGE_SIZE >= (vs.data?.total ?? 0))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </AsyncShell>

          <VirtualServerDetail
            open={vsDrawer != null}
            onClose={() => setVsDrawer(null)}
            mode={vsDrawer?.mode ?? 'detail'}
            id={vsDrawer?.mode === 'detail' ? vsDrawer?.id : undefined}
            onPersisted={(id) => setVsDrawer({ mode: 'detail', id })}
          />
        </SectionCard>
      )}
    </>
  );
}

/**
 * Members tab — manage who can sign into the admin console.
 * Only admins (or bootstrap admins from env) can mutate.
 */
function MembersTab(): JSX.Element {
  const { me } = useAuth();
  const members = usePortalMembers();
  const upsert = useUpsertPortalMember();
  const remove = useDeletePortalMember();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('viewer');
  const [actionError, setActionError] = useState<string | null>(null);
  // Filters
  const [filterText, setFilterText] = useState('');
  const [filterRole, setFilterRole] = useState<'' | 'admin' | 'viewer'>('');
  // Inline role-edit state per row.
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<'admin' | 'viewer'>('viewer');

  const isAdmin = me?.is_admin === true;

  async function onAdd(): Promise<void> {
    setActionError(null);
    if (!email.trim()) return;
    try {
      await upsert.mutateAsync({ email: email.trim().toLowerCase(), role });
      setEmail('');
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  async function onDelete(target: string): Promise<void> {
    setActionError(null);
    if (!confirm(`Remove ${target} from the portal allowlist?`)) return;
    try {
      await remove.mutateAsync(target);
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  return (
    <SectionCard
      icon={Users}
      title={`Portal members (${members.data?.items.length ?? 0})`}
      text="Stanford SSO confirms hd=stanford.edu, then this allowlist decides who can actually use the console. Admins can manage members; viewers are read-only."
    >
      {!isAdmin && (
        <p className="subtle" style={{ fontSize: 15, marginBottom: 12 }}>
          You're signed in as <code>{me?.email ?? '—'}</code> with role{' '}
          <strong>{me?.role ?? 'viewer'}</strong>. Adding/removing members requires the{' '}
          <strong>admin</strong> role.
        </p>
      )}

      {isAdmin && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
              Email
              <Required />
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@stanford.edu"
              style={inputStyle}
              required
            />
          </label>
          <label>
            <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
              Role
              <Required />
            </span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
              style={inputStyle}
            >
              <option value="viewer">viewer</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <button
            type="button"
            onClick={onAdd}
            disabled={!email.trim() || upsert.isPending}
            style={{ ...primaryBtnStyle, opacity: upsert.isPending ? 0.6 : 1 }}
          >
            <UserPlus size={14} style={{ marginRight: 6 }} />
            {upsert.isPending ? 'Adding…' : 'Add / update'}
          </button>
        </div>
      )}

      {actionError && (
        <p style={{ color: '#c0392b', fontSize: 15 }} role="alert">
          {actionError}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <input
          placeholder="Filter by email…"
          aria-label="Filter portal members by email"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 240px', minWidth: 0 }}
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value as '' | 'admin' | 'viewer')}
          aria-label="Filter portal members by role"
          style={inputStyle}
        >
          <option value="">All roles</option>
          <option value="admin">admin only</option>
          <option value="viewer">viewer only</option>
        </select>
      </div>

      <AsyncShell
        isLoading={members.isLoading}
        isError={members.isError}
        isEmpty={(members.data?.items.length ?? 0) === 0}
        error={members.error}
        loadingTitle="Loading members"
        errorTitle="Members API unreachable"
        emptyTitle="No members yet"
        emptyHint={<>Add the first portal member using the form above.</>}
      >
        <table className="data-table" style={{ fontSize: 15 }}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Added</th>
              <th>Last login</th>
              {isAdmin && <th style={{ width: 100 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(members.data?.items ?? [])
              .filter((m) => {
                if (filterRole && m.role !== filterRole) return false;
                if (filterText.trim()) {
                  const q = filterText.trim().toLowerCase();
                  if (!m.email.toLowerCase().includes(q)) return false;
                }
                return true;
              })
              .map((m) => {
                const added = m.added_at ?? m.addedAt ?? '';
                const last = m.last_login_at ?? m.lastLoginAt ?? null;
                const isEditingThis = editingEmail === m.email;
                return (
                  <tr key={m.email}>
                    <td>
                      <code>{m.email}</code>
                    </td>
                    <td>
                      {isEditingThis ? (
                        <select
                          value={editingRole}
                          onChange={(e) => setEditingRole(e.target.value as 'admin' | 'viewer')}
                          style={{ ...inputStyle, padding: '4px 8px' }}
                        >
                          <option value="viewer">viewer</option>
                          <option value="admin">admin</option>
                        </select>
                      ) : (
                        m.role
                      )}
                    </td>
                    <td>{added ? new Date(added).toLocaleString() : '—'}</td>
                    <td>{last ? new Date(last).toLocaleString() : 'never'}</td>
                    {isAdmin && (
                      <td style={{ display: 'flex', gap: 4 }}>
                        {isEditingThis ? (
                          <>
                            <button
                              type="button"
                              onClick={async () => {
                                setActionError(null);
                                try {
                                  await upsert.mutateAsync({
                                    email: m.email,
                                    role: editingRole,
                                  });
                                  setEditingEmail(null);
                                } catch (e) {
                                  setActionError(
                                    e instanceof ApiError
                                      ? `${e.status}: ${JSON.stringify(e.body)}`
                                      : (e as Error).message,
                                  );
                                }
                              }}
                              style={primaryBtnStyle}
                              title="Save role"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingEmail(null)}
                              style={dangerBtnStyle}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingEmail(m.email);
                                setEditingRole(m.role as 'admin' | 'viewer');
                              }}
                              style={{
                                ...dangerBtnStyle,
                                border: '1px solid var(--line-strong)',
                                color: 'inherit',
                              }}
                              title="Change role"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(m.email)}
                              style={dangerBtnStyle}
                              title="Remove member"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </AsyncShell>
    </SectionCard>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 15,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid rgba(140, 21, 21, 0.3)',
  background: 'linear-gradient(135deg, #8c1515, #b83a24)',
  color: '#fff8ec',
  fontWeight: 650,
  cursor: 'pointer',
  fontSize: 15,
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 12px 28px rgba(140, 21, 21, 0.22)',
};

const dangerBtnStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: 6,
  border: '1px solid #d97766',
  background: 'transparent',
  color: '#c0392b',
  cursor: 'pointer',
  fontSize: 14,
};

function pagerBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '4px 12px',
    borderRadius: 6,
    border: '1px solid var(--line)',
    background: disabled ? 'transparent' : 'rgba(255,255,255,0.6)',
    color: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontSize: 13,
  };
}
